import { parsePickupDate } from "../shared/order-domain";

export const DASH = '<span class="muted">—</span>';

let toastTimer: number | null = null;

export function element<T extends HTMLElement>(id: string): T {
  const value = document.getElementById(id);
  if (!value) throw new Error(`missing element: ${id}`);
  return value as T;
}

export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, (character) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
      character
    ] ?? character,
  );
}

export function formatMoney(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toFixed(2);
}

export function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, "0");
  const short = `${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return date.getFullYear() === new Date().getFullYear()
    ? short
    : `${date.getFullYear()}/${short}`;
}

export function formatDayShort(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
}

export function pickupDateHtml(pickup: string | null, orderTime: string | null): string {
  if (!pickup) return '<span class="pickup-date muted">—</span>';
  const match = /^\s*(\d{1,2})[.\/-](\d{1,2})\s*$/.exec(pickup);
  if (!match || !parsePickupDate(pickup, orderTime)) {
    return `<span class="pickup-date">${escapeHtml(pickup)}</span>`;
  }
  const pad = (part: string) => String(Number.parseInt(part, 10)).padStart(2, "0");
  return `<span class="pickup-date mono">${pad(match[1])}.${pad(match[2])}</span>`;
}

export function formatPickup(date: Date): string {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
}

export function suggestedPickup(orderTime: string): string {
  if (!orderTime) return "";
  const date = new Date(orderTime);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + 2);
  return formatPickup(date);
}

export function toLocalInput(value: string | null): string {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fileUrl(key: string): string {
  return `/api/files/${key.split("/").map(encodeURIComponent).join("/")}`;
}

export function showToast(message: string, isError = false): void {
  const toast = element<HTMLElement>("toast");
  toast.textContent = message;
  toast.classList.toggle("toast-error", isError);
  toast.hidden = false;
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.hidden = true;
  }, 3200);
}

export function setLoading(loading: boolean): void {
  element<HTMLElement>("ordersMain").classList.toggle("is-loading", loading);
  element<HTMLElement>("tableLoading").classList.toggle("is-visible", loading);
}

function syncModalState(): void {
  const hasOpenModal = Boolean(document.querySelector(".modal-backdrop:not([hidden])"));
  const app = document.querySelector<HTMLElement>(".app");
  if (app) app.inert = hasOpenModal;
  document.body.classList.toggle("modal-open", hasOpenModal);
}

export function openModal(modal: HTMLElement): void {
  modal.hidden = false;
  modal.setAttribute("aria-hidden", "false");
  syncModalState();
}

export function closeModal(modal: HTMLElement): void {
  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  syncModalState();
}

export function bindModalBackdrop(modal: HTMLElement, close: () => void): void {
  let pressedOnBackdrop = false;
  modal.addEventListener("mousedown", (event) => {
    pressedOnBackdrop = event.target === modal;
  });
  modal.addEventListener("click", (event) => {
    if (event.target === modal && pressedOnBackdrop) close();
    pressedOnBackdrop = false;
  });
}

export function hideAllModals(): void {
  document.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((modal) => {
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
  });
}
